import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EditModal, SummaryCard, useEditModal } from '@nucleus-commerce/admin-kit/forms';
import type { WeightUnitCode } from '@nucleus-commerce/core';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { COUNTRIES, countryName } from '@/features/markets';

import { useDefaultWeightUnit } from '../hooks/use-default-weight-unit';
import { useProductScope } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { CardEditAction, SharedChip } from './product-scope';
import { WeightInput } from './weight-input';

const NO_COUNTRY = '__none__';

/** Weight, HS code and country of origin: what shipping rates and customs forms read. */
export function ShippingSection() {
  const { t } = useTranslation('products');
  const { readOnly } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  const defaultUnit = useDefaultWeightUnit();
  const edit = useEditModal({ form, fields: ['weight', 'hsCode', 'countryOfOrigin'] });
  const [weight, hsCode, country] = useWatch({
    control: form.control,
    name: ['weight', 'hsCode', 'countryOfOrigin'],
  });
  const customs = [
    hsCode.trim() ? t('rail.shipping.hsSummary', { code: hsCode.trim() }) : '',
    country.trim() ? t('rail.shipping.originSummary', { code: country.trim().toUpperCase() }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const weightText = weight.value.trim();
  const title = t('sections.shipping');

  return (
    <SummaryCard
      title={title}
      badge={<SharedChip />}
      action={
        <CardEditAction
          onEdit={edit.show}
          label={
            <>
              {t('scope.edit')} <span className="sr-only">{title}</span>
            </>
          }
        />
      }
    >
      {weightText ? (
        <span className="text-[13px]">
          {t('rail.shipping.weightSummary', {
            value: weightText,
            unit: weight.unit || defaultUnit,
          })}
        </span>
      ) : null}
      {customs ? <span className="text-muted-foreground text-[13px]">{customs}</span> : null}
      {!weightText && !customs ? (
        <span className="text-muted-foreground text-[13px]">{t('rail.notSet')}</span>
      ) : null}
      {readOnly ? null : (
        <EditModal
          {...edit.modal}
          size="lg"
          title={title}
          description={t('rail.shipping.description')}
        >
          <ShippingFields defaultUnit={defaultUnit} />
        </EditModal>
      )}
    </SummaryCard>
  );
}

function ShippingFields({ defaultUnit }: { defaultUnit: WeightUnitCode }) {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  return (
    <>
      <div className="flex flex-col gap-2.5">
        <div>
          <h3 className="text-sm font-semibold">{t('rail.shipping.weightHeading')}</h3>
          <p className="text-muted-foreground mt-0.5 text-[13px]">
            {t('rail.shipping.weightHint')}
          </p>
        </div>
        <FormField
          control={form.control}
          name="weight"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>{t('rail.shipping.productWeight')}</FormLabel>
              <FormControl>
                <WeightInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  defaultUnit={defaultUnit}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-2.5 border-t pt-4">
        <h3 className="text-sm font-semibold">{t('rail.shipping.customsHeading')}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="hsCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.hsCode')}</FormLabel>
                <FormControl>
                  <Input {...field} className="font-mono text-[13px]" />
                </FormControl>
                <FormDescription className="text-xs">{t('rail.shipping.hsHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="countryOfOrigin"
            render={({ field }) => {
              const code = field.value.trim().toUpperCase();
              // A saved code outside the list stays selectable, so opening the modal changes nothing.
              const listed = COUNTRIES.some((country) => country.code === code);
              return (
                <FormItem>
                  <FormLabel>{t('rail.shipping.country')}</FormLabel>
                  <Select
                    value={code || NO_COUNTRY}
                    onValueChange={(next) => field.onChange(next === NO_COUNTRY ? '' : next)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full" onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_COUNTRY}>
                        <span className="text-muted-foreground">{t('rail.notSet')}</span>
                      </SelectItem>
                      {code && !listed ? (
                        <SelectItem value={code}>{countryName(code)}</SelectItem>
                      ) : null}
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </div>
    </>
  );
}
